import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { message, Button, Dropdown, Menu, Tooltip, Spin, Input, Modal } from 'antd';
import ZoomVideo from '@zoom/videosdk';
import {
  getDetailSession,
  getTokenSession,
  verifyJoinSession,
  moderateText,
  logSensitiveKeywordEvent,
} from 'redux/actions/mvp/session.action';
import { fetchUserMvp } from 'redux/actions/user.action';
import { I18n } from 'helpers/i18n';
import sensitiveKeywordsConfig from '../../../../config/sensitiveKeywords.json';
import './session-video-custom.scss';

/** Bật true để xem console debug của Live Transcript / từ khóa nhạy cảm. */
const DEBUG_SENSITIVE_KEYWORD = false;

/**
 * Kích thước tối đa của rolling buffer gửi cho L2 (đồng bộ kL2TextTailMax bản macOS).
 * Zoom cắt caption theo từng câu nói, nên L2 cần nhiều lượt hội thoại mới đủ ngữ cảnh
 * để phân biệt "hỏi số tài khoản để chuyển tiền" với "bàn về tài khoản ngân hàng nói chung".
 */
const L2_TEXT_TAIL_MAX = 2000;
/** Khoảng cách tối thiểu giữa 2 lần gọi L2 (đồng bộ kL2MinRequestInterval bản macOS). */
const L2_MIN_REQUEST_INTERVAL_MS = 3000;
/** Sau khi hiện một cảnh báo, tạm ngưng cảnh báo tiếp theo (đồng bộ cooldown 45s bản macOS). */
const ALERT_COOLDOWN_MS = 45000;
/** Số câu tối đa giữ trong buffer — chặn mảng phình vô hạn trong phiên dài. */
const MAX_TRANSCRIPT_SEGMENTS = 40;

/**
 * Zoom Live Transcription bắn caption TẠM THỜI: cùng một câu được gửi lại nhiều lần, mỗi lần
 * dài thêm ("Hi, I just" → "Hi, I just got a text with" → "Hi, I just got a text with a 6-").
 * Nối thẳng từng lần vào buffer sẽ nhân bản nội dung theo cấp số nhân và đẩy ngữ cảnh thật
 * ra khỏi cửa sổ L2_TEXT_TAIL_MAX.
 *
 * Dedupe theo `msgId` (ID ổn định của một câu, theo LiveTranscriptionMessage của SDK). Khi SDK
 * không gửi msgId thì nhận diện bằng quan hệ tiền tố với câu ngay trước.
 */
function upsertTranscriptSegment(segments, line, msgId) {
  if (msgId) {
    const existing = segments.find((s) => s.msgId === msgId);
    if (existing) {
      existing.text = line;
      return segments;
    }
  } else {
    const last = segments[segments.length - 1];
    if (last && !last.msgId && (line.startsWith(last.text) || last.text.startsWith(line))) {
      // Cùng một câu đang được đọc dở — giữ bản dài hơn thay vì thêm dòng mới.
      if (line.length >= last.text.length) last.text = line;
      return segments;
    }
  }
  segments.push({ msgId: msgId || null, text: line });
  if (segments.length > MAX_TRANSCRIPT_SEGMENTS) {
    segments.splice(0, segments.length - MAX_TRANSCRIPT_SEGMENTS);
  }
  return segments;
}

/** Ghép các câu đã dedupe thành payload gửi L2, giữ phần đuôi tối đa L2_TEXT_TAIL_MAX ký tự. */
function buildRollingText(segments) {
  const text = segments.map((s) => s.text).join('\n');
  return text.length > L2_TEXT_TAIL_MAX ? text.slice(text.length - L2_TEXT_TAIL_MAX) : text;
}

/** Kiểm tra transcript có chứa từ khóa trong danh sách JSON không (L1). Trả { detected, category, keyword }. */
function detectSensitiveByJson(text) {
  if (!text || !sensitiveKeywordsConfig.categories || !Array.isArray(sensitiveKeywordsConfig.categories)) {
    return { detected: false };
  }
  const lower = String(text).toLowerCase();
  for (const cat of sensitiveKeywordsConfig.categories) {
    const keywords = Array.isArray(cat.keywords) ? cat.keywords : [];
    // Thử keyword dài trước (khớp semantics firstMatchInText của macOS/ced-web).
    const sorted = [...keywords].sort((a, b) => String(b).length - String(a).length);
    for (const kw of sorted) {
      if (kw && lower.includes(String(kw).toLowerCase())) {
        return { detected: true, category: cat.name, keyword: kw };
      }
    }
  }
  return { detected: false };
}
import {
  AudioMutedOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  ShareAltOutlined,
  StopOutlined,
  MessageOutlined,
  PhoneOutlined,
  MoreOutlined,
  LoadingOutlined,
  UserOutlined,
  CloseOutlined,
} from '@ant-design/icons';

function SessionVideoCustom({
  history,
  match,
  user,
  getDetailSession,
  getTokenSession,
  verifyJoinSession,
  moderateText: moderateTextAction,
  logSensitiveKeywordEvent: logSensitiveKeywordEventAction,
}) {
  const sessionId = match.params.id;
  const [client, setClient] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const userVideoRefs = useRef({});
  const shareCanvasRef = useRef(null);
  const shareVideoRef = useRef(null);
  const [shareRenderType, setShareRenderType] = useState('canvas'); // 'canvas' | 'video'
  const [activeShareUser, setActiveShareUser] = useState(null);
  const [pinnedUser, setPinnedUser] = useState(null);
  const [showUserList, setShowUserList] = useState(false); // Control user list panel visibility
  const [showChat, setShowChat] = useState(false); // Control chat panel visibility
  const [chatMessages, setChatMessages] = useState([]); // Zoom chat messages
  const [chatInput, setChatInput] = useState('');
  const [chatClient, setChatClient] = useState(null);
  const [chatToast, setChatToast] = useState(null); // { sender, text }

  const pinnedUserRef = useRef(null);
  const activeShareUserRef = useRef(null);
  const chatToastTimeoutRef = useRef(null);

  // Sensitive content (join session) — port từ ced-web
  const [sensitiveModal, setSensitiveModal] = useState(null); // { keyword, category, speaker, transcript, source } | null
  const [sensitiveModalSubmitting, setSensitiveModalSubmitting] = useState(false);
  const sessionUserIdRef = useRef(null);
  const sensitiveCheckInFlightRef = useRef(false);
  /** Ngữ cảnh hội thoại đã dedupe, gửi cho L2 thay vì 1 dòng caption lẻ. */
  const rollingSegmentsRef = useRef([]);
  /** Thời điểm gọi L2 gần nhất — dùng cho debounce. */
  const lastL2RequestAtRef = useRef(0);
  /** Thời điểm hiện cảnh báo gần nhất — dùng cho cooldown. */
  const lastAlertAtRef = useRef(0);
  /** Timer flush buffer khi lần check bị hoãn vì debounce/inFlight (tránh mất caption cuối). */
  const pendingL2TimerRef = useRef(null);
  /** Kết quả L1 đang chờ L2 xác nhận — dùng làm fallback khi L2 không trả lời được. */
  const pendingL1MatchRef = useRef(null);
  /** Đọc trạng thái modal trong callback caption mà không cần re-register listener. */
  const sensitiveModalOpenRef = useRef(false);
  /** Component còn mounted — chặn setState/timer sau khi rời trang. */
  const mountedRef = useRef(true);
  const isAudioEnabledRef = useRef(true);
  const isVideoEnabledRef = useRef(true);
  const mediaStateBeforeSensitiveModalRef = useRef(null);

  // Video resolution constant
  const RESOLUTION = { width: 640, height: 360 };

  useEffect(() => {
    document.title = `TECD - ${I18n.t('tecd_slogan')}`;
  }, []);

  useEffect(() => {
    pinnedUserRef.current = pinnedUser;
  }, [pinnedUser]);

  useEffect(() => {
    activeShareUserRef.current = activeShareUser;
  }, [activeShareUser]);

  // Sync mic/cam state vào ref để đọc trong callback caption.
  useEffect(() => {
    isAudioEnabledRef.current = isAudioEnabled;
    isVideoEnabledRef.current = isVideoEnabled;
  }, [isAudioEnabled, isVideoEnabled]);

  const joinSession = async () => {
    setLoading(true);
    try {
      const sessionInfo = await getDetailSession({ sessionId });
      if (!sessionInfo) throw new Error('No session info');

      const verifyData = await verifyJoinSession({
        sessionId: sessionInfo.sessionUserId,
        sessionUserId: sessionInfo.sessionUserId,
      });
      if (!verifyData || verifyData.status !== 'OK') {
        throw new Error('Unable to join session. Please check your permission or session status.');
      }

      let infoZoom = verifyData.infoZoom;
      if (typeof infoZoom === 'string') infoZoom = JSON.parse(infoZoom);
      if (!infoZoom) throw new Error('No Zoom info');

      const tokenData = await getTokenSession({ sessionName: infoZoom.sessionName });
      const token = typeof tokenData === 'string' ? tokenData : tokenData?.token || tokenData?.data;
      if (!token) throw new Error('No token');
      const userName = user?.name || 'Anonymous User';
      const sessionPasscode = infoZoom.password || '';

      const client = ZoomVideo.createClient();
      await client.init('en-US', 'Global');
      // ZoomVideo.createClient() trả về CÙNG một instance mỗi lần gọi (singleton theo tài liệu SDK),
      // nên client này có thể vẫn đang ở trong phiên trước. Phải rời phiên cũ TRƯỚC khi join —
      // và phải await: gọi leave() kiểu fire-and-forget rồi join ngay sẽ tạo race và Zoom trả
      // "duplicated operation". Client chưa từng join thì leave() reject, nuốt lỗi là đúng.
      try {
        await client.leave();
      } catch (leaveErr) {
        console.warn('[Join session] leave() trước khi join (bỏ qua, thường do chưa ở trong phiên nào):', leaveErr);
      }
      await client.join(infoZoom.sessionName, token, userName, sessionPasscode);
      setClient(client);

      // Init chat client after join
      try {
        const chat = client.getChatClient();
        setChatClient(chat);

        // Optionally load existing chat history
        try {
          const history = await chat.getHistory();
          if (Array.isArray(history)) {
            const currentUserId = client.getCurrentUserInfo()?.userId;
            const mapped = history
              .filter((item) => item && (item.message || item.content))
              .map((item, idx) => ({
                id: item.id || `${item.timestamp || ''}-${idx}`,
                sender:
                  item.sender?.displayName ||
                  item.sender?.userName ||
                  item.sender?.userId ||
                  'Unknown',
                text: item.message || item.content || '',
                timestamp: item.timestamp || new Date().toISOString(),
                isSelf: item.sender?.userId === currentUserId,
              }));
            setChatMessages(mapped);
          }
        } catch (e) {
          console.warn('Error getting history:', e);
        }
      } catch (e) {
        console.warn('Chat client not available:', e);
      }

      // Start local media (video & audio)
      const stream = client.getMediaStream();
      try {
        await stream.startVideo();
      } catch (e) {
        setIsVideoEnabled(false);
      }

      try {
        await stream.startAudio();
      } catch (e) {
        setIsAudioEnabled(false);
      }

      const users = client.getAllUser();
      setAllUsers(users);

      users.forEach((user) => {
        if (user.sharerOn) {
          setActiveShareUser(user);
          if (shareCanvasRef.current) {
            stream.startShareView(shareCanvasRef.current, user.userId);
          }
        }
      });

      // Listen for remote user join and video events
      client.on('user-added', (payload) => {
        // const currentUserId = client.getCurrentUserInfo().userId;
        // payload.forEach((item) => {
        //   if (item.userId !== currentUserId) message.info(`${item.displayName} joined the session.`);
        // });
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('user-removed', (payload) => {
        payload.forEach((item) => {
          // message.info(`${item.displayName} left the session.`);

          if (pinnedUserRef.current && pinnedUserRef.current.userId === item.userId) {
            setPinnedUser(null);
          }

          if (activeShareUserRef.current && activeShareUserRef.current.userId === item.userId) {
            setActiveShareUser(null);
            setShareRenderType('canvas');
            try {
              const stream = client.getMediaStream();
              stream.stopShareView();
            } catch (e) {
              console.warn('Could not stop share view:', e);
            }
          }
        });
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('user-updated', (payload) => {
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('passively-stop-share', (payload) => {
        setActiveShareUser(null);
        setShareRenderType('canvas');
      });

      // ===== Sensitive content pipeline (join session) =====
      sessionUserIdRef.current = sessionInfo.sessionUserId;

      const showSensitiveModal = (info) => {
        const modalState = {
          keyword: info.keyword || info.category || 'sensitive',
          category: info.category || 'sensitive',
          speaker: info.speakerName,
          transcript: info.text,
          source: info.source,
          confidence: info.confidence != null ? info.confidence : null,
        };
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive] Hiển thị modal:', {
            source: info.source,
            category: modalState.category,
            confidence: modalState.confidence,
          });
        }
        // Nội dung đã được cảnh báo → xóa buffer để lần check sau không báo lại chính đoạn này.
        rollingSegmentsRef.current = [];
        pendingL1MatchRef.current = null;
        lastAlertAtRef.current = Date.now();
        sensitiveModalOpenRef.current = true;
        try {
          // Lưu trạng thái mic/cam trước khi tạm dừng để khôi phục khi user tiếp tục.
          mediaStateBeforeSensitiveModalRef.current = {
            audio: isAudioEnabledRef.current,
            video: isVideoEnabledRef.current,
          };
          const stream = client.getMediaStream();
          const currentUserId = client.getCurrentUserInfo()?.userId;
          if (stream) {
            stream.muteAudio().catch(() => {});
            stream.stopVideo().catch(() => {});
            if (currentUserId) {
              try {
                stream.detachVideo(currentUserId);
              } catch (detachErr) {
                // ignore
              }
            }
          }
          setIsAudioEnabled(false);
          setIsVideoEnabled(false);
        } catch (e) {
          if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Sensitive] Không tạm dừng được media:', e);
        }
        setSensitiveModal(modalState);
      };

      /** Hoãn lần check sang thời điểm hết ràng buộc, thay vì vứt caption đi. */
      const scheduleSensitiveCheck = (speakerName, forceNow, delayMs) => {
        if (pendingL2TimerRef.current) clearTimeout(pendingL2TimerRef.current);
        pendingL2TimerRef.current = setTimeout(() => {
          pendingL2TimerRef.current = null;
          if (!mountedRef.current) return;
          runSensitiveCheck(speakerName, forceNow);
        }, Math.max(delayMs, 50));
      };

      /**
       * Gọi L2 trên rolling buffer và quyết định có cảnh báo hay không.
       * L1 KHÔNG còn tự quyết định — nó chỉ (a) ép bỏ qua debounce khi trúng từ khóa,
       * (b) làm fallback khi L2 không trả lời được (engineStatus != 'ok').
       */
      function runSensitiveCheck(speakerName, forceNow) {
        if (!mountedRef.current) return;
        if (sensitiveModalOpenRef.current) return;
        const sinceAlert = Date.now() - lastAlertAtRef.current;
        if (lastAlertAtRef.current && sinceAlert < ALERT_COOLDOWN_MS) {
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Sensitive] Bỏ qua: còn cooldown', { remainingMs: ALERT_COOLDOWN_MS - sinceAlert });
          }
          return;
        }
        const bufferText = buildRollingText(rollingSegmentsRef.current);
        if (!bufferText || !bufferText.trim()) return;

        if (sensitiveCheckInFlightRef.current) {
          // Caption vẫn nằm trong buffer nên không mất — chỉ cần thử lại sau.
          scheduleSensitiveCheck(speakerName, forceNow, L2_MIN_REQUEST_INTERVAL_MS);
          return;
        }
        const sinceLast = Date.now() - lastL2RequestAtRef.current;
        if (!forceNow && lastL2RequestAtRef.current && sinceLast < L2_MIN_REQUEST_INTERVAL_MS) {
          scheduleSensitiveCheck(speakerName, forceNow, L2_MIN_REQUEST_INTERVAL_MS - sinceLast);
          return;
        }

        const l1Match = pendingL1MatchRef.current;
        sensitiveCheckInFlightRef.current = true;
        lastL2RequestAtRef.current = Date.now();
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive] Gọi moderate-text (L2):', {
            bufferLength: bufferText.length,
            preview: bufferText.substring(0, 80),
            l1Matched: !!l1Match,
            forceNow,
          });
        }

        const thunk = moderateTextAction && moderateTextAction({ text: bufferText });
        const promise = typeof thunk === 'function' ? thunk() : thunk;
        (promise || Promise.resolve(null))
          .then((res) => {
            if (!mountedRef.current) return;
            const data = res && (res.data !== undefined ? res.data : res);
            // Không có payload → coi như L2 không trả lời được, KHÔNG coi là an toàn.
            const engineStatus = data ? data.engineStatus || 'ok' : 'error';
            const l2Available = engineStatus === 'ok';
            const l2Detected = !!(data && data.detected === true);

            if (DEBUG_SENSITIVE_KEYWORD) {
              console.log('[Sensitive] Kết quả L2:', {
                engineStatus,
                detected: l2Detected,
                category: data ? data.category : null,
                confidence: data ? data.confidence : null,
              });
            }

            if (l2Available) {
              if (l2Detected) {
                showSensitiveModal({
                  keyword: (data && data.keyword) || (l1Match && l1Match.keyword) || null,
                  category: (data && data.category) || 'sensitive',
                  speakerName,
                  text: bufferText,
                  source: l1Match ? 'l1+l2' : 'l2',
                  confidence: data ? data.confidence : null,
                });
              } else {
                // L2 đã xét và kết luận an toàn → tin L2, kể cả khi L1 trúng từ khóa.
                // Đây là nhánh gỡ false positive cho các câu kiểu
                // "An OTP adds another layer of security" / "Keeping your CVV private is important".
                pendingL1MatchRef.current = null;
              }
              return;
            }

            // L2 không dùng được (thiếu API key / lỗi mạng) → fallback về L1 để không mất lưới đỡ.
            if (l1Match) {
              if (DEBUG_SENSITIVE_KEYWORD) {
                console.warn('[Sensitive] L2 không khả dụng → fallback cảnh báo theo L1.', {
                  engineStatus,
                  keyword: l1Match.keyword,
                });
              }
              showSensitiveModal({
                keyword: l1Match.keyword,
                category: l1Match.category,
                speakerName: l1Match.speakerName || speakerName,
                text: bufferText,
                source: 'l1-fallback',
                confidence: null,
              });
            }
          })
          .catch((err) => {
            if (!mountedRef.current) return;
            if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Sensitive] Lỗi moderate-text:', err);
            // Lỗi ở tầng client cũng là "L2 không trả lời được" → fallback L1.
            const l1Fallback = pendingL1MatchRef.current;
            if (l1Fallback) {
              showSensitiveModal({
                keyword: l1Fallback.keyword,
                category: l1Fallback.category,
                speakerName: l1Fallback.speakerName || speakerName,
                text: bufferText,
                source: 'l1-fallback',
                confidence: null,
              });
            }
          })
          .finally(() => {
            sensitiveCheckInFlightRef.current = false;
          });
      }

      const handleTranscriptPayload = (eventName, payload) => {
        const textRaw =
          payload &&
          (payload.text ?? payload.message ?? payload.content ?? payload.transcript ?? payload.messageContent);
        const text = textRaw != null ? String(textRaw).trim() : '';
        const speakerName =
          (payload && (payload.displayName || payload.userName || payload.speakerName || payload.speaker)) ||
          'Unknown';
        // msgId định danh MỘT câu qua nhiều lần bắn caption tạm thời; done=true là câu đã nói xong.
        const msgId = (payload && (payload.msgId || payload.messageId)) || null;
        const isFinal = !payload || payload.done !== false;

        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive] caption:', {
            event: eventName,
            speaker: speakerName,
            msgId,
            done: payload && payload.done,
            text: text.substring(0, 80),
          });
        }
        if (!text) return;

        upsertTranscriptSegment(rollingSegmentsRef.current, text, msgId);

        // L1 chỉ là tín hiệu phụ: đánh dấu để (a) bỏ qua debounce, (b) fallback nếu L2 chết.
        const jsonResult = detectSensitiveByJson(text);
        if (jsonResult.detected) {
          pendingL1MatchRef.current = {
            keyword: jsonResult.keyword,
            category: jsonResult.category,
            speakerName,
          };
        }

        // Caption tạm thời vẫn được gom vào buffer ở trên, nhưng chưa gọi L2: phân loại một câu
        // đang nói dở ("The code is") vừa tốn token vừa dễ sai. Chờ câu nói xong rồi mới xét.
        // SDK không gửi `done` thì coi như đã xong, để không bao giờ tắt hẳn việc kiểm duyệt.
        if (!isFinal) return;

        runSensitiveCheck(speakerName, jsonResult.detected);
      };

      client.on('caption-message', (payload) => handleTranscriptPayload('caption-message', payload));
      try {
        client.on('live-transcription-message-received', (payload) =>
          handleTranscriptPayload('live-transcription-message-received', payload),
        );
      } catch (e) {
        // SDK có thể không có event này
      }

      // Tự thử bật Live Transcription (Zoom chỉ cho host bật; không phải host sẽ lỗi 7300).
      try {
        const lttClient =
          client.getLiveTranscriptionClient && typeof client.getLiveTranscriptionClient === 'function'
            ? client.getLiveTranscriptionClient()
            : null;
        if (lttClient && typeof lttClient.startLiveTranscription === 'function') {
          const result = lttClient.startLiveTranscription();
          if (result && typeof result.then === 'function') {
            result.catch((err) => {
              if (DEBUG_SENSITIVE_KEYWORD) console.log('[Live Transcript] Không bật được (có thể cần host bật):', err);
            });
          }
        }
      } catch (e) {
        if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Live Transcript] Lỗi khi bật:', e);
      }
    } catch (e) {
      // Zoom SDK không ném Error mà ném ExecutedFailure { type, reason, errorCode } — không có
      // .message. Nếu không log riêng thì mọi lỗi SDK đều hiện ra dưới dạng message chung chung
      // và nguyên nhân thật bị mất hoàn toàn.
      console.error('[Join session] Thất bại:', {
        message: e && e.message,
        type: e && e.type,
        reason: e && e.reason,
        errorCode: e && e.errorCode,
        error: e,
      });
      message.error((e && e.message) || I18n.t('failed_join_session'));
      history.push('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    sensitiveModalOpenRef.current = !!sensitiveModal;
  }, [sensitiveModal]);

  // Tự động join session khi vào trang
  useEffect(() => {
    mountedRef.current = true;
    joinSession();
    return () => {
      mountedRef.current = false;
      if (pendingL2TimerRef.current) {
        clearTimeout(pendingL2TimerRef.current);
        pendingL2TimerRef.current = null;
      }
    };
  }, []);

  // Rời session
  const leaveSession = async () => {
    if (client) {
      await client.leave();
      setClient(null);
      history.push(`/session-detail/${sessionId}`);
    }
  };

  // Bật/tắt audio
  const toggleAudio = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const currentUserId = client.getCurrentUserInfo().userId;

    if (isAudioEnabled) {
      try {
        await stream.muteAudio();
        setIsAudioEnabled(false);
        // Update user's muted status in allUsers
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: true } : user)),
        );
      } catch (e) {
        console.warn('Could not mute audio:', e);
        message.warning(I18n.t('could_not_mute_audio'));
      }
    } else {
      try {
        await stream.unmuteAudio();
        setIsAudioEnabled(true);
        // Update user's muted status in allUsers
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: false } : user)),
        );
      } catch (e) {
        console.warn('Could not unmute audio:', e);
        message.warning(I18n.t('could_not_unmute_audio'));
      }
    }
  };

  // Helper: Start local video and attach to container
  const startVideo = async (stream, userId) => {
    try {
      await stream.startVideo();
      let userVideo = await stream.attachVideo(userId, RESOLUTION);
      document.querySelector('video-player-container').appendChild(userVideo);
    } catch (e) {
      console.warn('Could not start video:', e);
      message.warning(I18n.t('could_not_start_video'));
      throw e; // Re-throw to let toggleVideo handle it
    }
  };

  // Helper: Start remote video and attach to container
  const startUserVideo = async (stream, userId, ref) => {
    if (!stream || !userId || !ref) {
      return;
    }
    // Detach video trước nếu đã từng attach
    try {
      stream.detachVideo(userId);
    } catch (e) {
      // ignore nếu chưa từng attach
    }
    ref.innerHTML = '';
    try {
      let userVideo = await stream.attachVideo(userId, RESOLUTION);
      ref.appendChild(userVideo);
    } catch (e) {
      console.warn(`Could not attach video for user ${userId}:`, e);
    }
  };

  // Helper: Stop local video and detach from container
  const stopVideo = async (stream, userId) => {
    try {
      await stream.stopVideo();
      stream.detachVideo(userId);
    } catch (e) {
      console.warn('Could not stop video:', e);
    }
  };

  // Bật/tắt video
  const toggleVideo = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const userId = client.getCurrentUserInfo().userId;
    if (isVideoEnabled) {
      try {
        await stopVideo(stream, userId);
        setIsVideoEnabled(false);
      } catch (e) {
        console.warn('Could not stop video:', e);
        message.warning(I18n.t('could_not_stop_video'));
      }
    } else {
      try {
        await startVideo(stream, userId);
        setIsVideoEnabled(true);
      } catch (e) {
        console.warn('Could not start video:', e);
        // Don't show error message here as startVideo already shows it
      }
    }
  };

  // Xử lý lựa chọn của user trên modal cảnh báo nhạy cảm.
  const handleSensitiveModalChoice = async (type) => {
    if (!sensitiveModal || sensitiveModalSubmitting) return;
    const sessionUserId = sessionUserIdRef.current;
    if (!sessionUserId) {
      message.error(I18n.t('video_session_info_not_found'));
      return;
    }
    setSensitiveModalSubmitting(true);
    const params = {
      session_user_id: sessionUserId,
      speakerName: sensitiveModal.speaker,
      keyword: sensitiveModal.keyword,
      category: sensitiveModal.category,
      type,
      transcript: sensitiveModal.transcript || '',
      source: sensitiveModal.source || 'unknown',
      // Gửi confidence của L2 để tune ngưỡng từ dữ liệu production (null khi cảnh báo đến từ L1 fallback).
      confidence: sensitiveModal.confidence != null ? sensitiveModal.confidence : null,
    };
    try {
      const thunk = logSensitiveKeywordEventAction && logSensitiveKeywordEventAction(params);
      const promise = typeof thunk === 'function' ? thunk() : thunk;
      await (promise || Promise.resolve(null));
    } catch (e) {
      console.warn('logSensitiveKeywordEvent error:', e);
    }
    setSensitiveModal(null);
    setSensitiveModalSubmitting(false);

    // end_session: rời phiên ngay.
    if (type === 'end_session') {
      await leaveSession();
      return;
    }

    // SAFE_CONFIRMED / ACCIDENTAL_MENTION: khôi phục mic/cam như trước khi mở modal.
    const savedMedia = mediaStateBeforeSensitiveModalRef.current;
    mediaStateBeforeSensitiveModalRef.current = null;
    if (client && savedMedia) {
      try {
        const stream = client.getMediaStream();
        const userId = client.getCurrentUserInfo()?.userId;
        if (stream) {
          if (savedMedia.audio) {
            await stream.unmuteAudio();
            setIsAudioEnabled(true);
          } else {
            await stream.muteAudio();
            setIsAudioEnabled(false);
          }
          if (userId) {
            if (savedMedia.video) {
              await startVideo(stream, userId);
              setIsVideoEnabled(true);
            } else {
              await stopVideo(stream, userId);
              setIsVideoEnabled(false);
            }
          }
        }
      } catch (e) {
        console.warn('Could not restore media after sensitive modal:', e);
      }
    }
  };

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();
    allUsers.forEach((user) => {
      if (!userVideoRefs.current[user.userId]) {
        return;
      }
      startUserVideo(stream, user.userId, userVideoRefs.current[user.userId]);
    });
  }, [allUsers, client, pinnedUser]);

  // share screen
  const handleStartShare = async () => {
    if (!client) return;
    const currentUser = client.getCurrentUserInfo();
    setActiveShareUser(currentUser);

    const stream = client.getMediaStream();
    const userId = currentUser.userId;

    // Tự động tắt camera khi bật sharing
    if (isVideoEnabled) {
      try {
        await stopVideo(stream, userId);
        setIsVideoEnabled(false);
      } catch (e) {
        console.warn('Could not stop video when starting share:', e);
      }
    }

    try {
      if (stream.isStartShareScreenWithVideoElement()) {
        setShareRenderType('video');
        await stream.startShareScreen(shareVideoRef.current, { enableOptimizeForSharedVideo: true });
      } else {
        setShareRenderType('canvas');
        await stream.startShareScreen(shareCanvasRef.current, { enableOptimizeForSharedVideo: true });
      }
    } catch (e) {
      message.error(e.message || I18n.t('failed_start_screen_share'));
    }
  };

  // stop share screen
  const handleStopShare = async () => {
    if (!client) return;
    setActiveShareUser(null);
    setShareRenderType('canvas');
    const stream = client.getMediaStream();
    try {
      await stream.stopShareScreen();
    } catch (e) {
      message.error(e.message || I18n.t('failed_stop_screen_share'));
    }
  };

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();
    const onActiveShareChange = (payload) => {
      if (payload.state === 'Active') {
        const user = allUsers.find((u) => u.userId === payload.userId);
        setActiveShareUser(user || { userId: payload.userId });
        if (shareCanvasRef.current) {
          stream.startShareView(shareCanvasRef.current, payload.userId);
        }
      } else if (payload.state === 'Inactive') {
        setActiveShareUser(null);
        setShareRenderType('canvas');
        stream.stopShareView();
      }
    };
    client.on('active-share-change', onActiveShareChange);
    return () => {
      client.off('active-share-change', onActiveShareChange);
    };
  }, [client, allUsers]);

  const renderUserBox = (user, isMain = false) => {
    const isPinned = pinnedUser && pinnedUser.userId === user.userId;
    const menu = (
      <Menu>
        <Menu.Item key="pin" onClick={() => setPinnedUser(isPinned ? null : user)}>
          {isPinned ? I18n.t('unpin_screen') : I18n.t('pin_screen')}
        </Menu.Item>
      </Menu>
    );
    return (
      <div key={user.userId} className={`session-video-user-box${isMain ? ' main' : ''}`}>
        <Dropdown overlay={menu} trigger={['click']}>
          <Button shape="circle" icon={<MoreOutlined />} size="small" className="session-video-user-menu" />
        </Dropdown>
        <div className="session-video-username">
          {user.displayName || user.userId}
          {user.userId === client?.getCurrentUserInfo()?.userId ? ` ${I18n.t('you_label')}` : ''}
          <span className="user-status-icon audio">
            <AudioOutlined
              style={{
                color: user.muted ? '#e53935' : '#4caf50',
                fontSize: 16,
              }}
            />
            {user.muted && <span className="icon-slash" />}
          </span>
        </div>
        {user.isVideoOn || user.bVideoOn ? (
          <video-player-container
            ref={(el) => (userVideoRefs.current[user.userId] = el)}
            className="session-video-player"
          ></video-player-container>
        ) : (
          <div className="session-video-avatar-icon">
            <UserOutlined style={{ fontSize: 40, color: '#fff' }} />
          </div>
        )}
      </div>
    );
  };

  // Render user list item for the side panel
  const renderUserListItem = (user) => {
    const isPinned = pinnedUser && pinnedUser.userId === user.userId;
    const isCurrentUser = user.userId === client?.getCurrentUserInfo()?.userId;

    const menu = (
      <Menu>
        <Menu.Item key="pin" onClick={() => setPinnedUser(isPinned ? null : user)}>
          {isPinned ? I18n.t('unpin_screen') : I18n.t('pin_screen')}
        </Menu.Item>
      </Menu>
    );

    return (
      <div key={user.userId} className="user-list-item">
        <div className="user-item-name">
          <div className="user-item-avatar-icon">
            <UserOutlined style={{ fontSize: 16, color: '#333' }} />
          </div>
          {user.displayName || user.userId}
          {isCurrentUser && ` ${I18n.t('you_label')}`}
        </div>
        <div className="user-item-status">
          <span className="user-status-icon audio">
            <AudioOutlined
              style={{
                color: user.muted ? '#e53935' : '#4caf50',
              }}
            />
            {user.muted && <span className="icon-slash" />}
          </span>
          <span className="user-status-icon video">
            <VideoCameraOutlined
              style={{
                color: user.isVideoOn || user.bVideoOn ? '#4caf50' : '#e53935',
              }}
            />
            {!(user.isVideoOn || user.bVideoOn) && <span className="icon-slash" />}
          </span>
          {isPinned && (
            <span className="pin-indicator" style={{ color: '#1890ff', fontSize: 12 }}>
              📌
            </span>
          )}
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} size="small" className="user-item-menu-button" />
          </Dropdown>
        </div>
      </div>
    );
  };

  const mainContent = pinnedUser || activeShareUser;
  const currentUserId = client?.getCurrentUserInfo()?.userId;
  const isCurrentUserSharing = activeShareUser && activeShareUser.userId === currentUserId;
  const isAnyoneSharing = !!activeShareUser;
  const isOtherUserSharing = isAnyoneSharing && !isCurrentUserSharing;

  // Clear chat toast timeout on unmount
  useEffect(() => {
    return () => {
      if (chatToastTimeoutRef.current) {
        clearTimeout(chatToastTimeoutRef.current);
      }
    };
  }, []);

  // Listen for incoming Zoom chat messages
  useEffect(() => {
    if (!client) return;

    const onChatMessage = (payload) => {
      try {
        const currentUserIdInner = client.getCurrentUserInfo()?.userId;
        const senderInfo = payload?.sender || {};
        const text = payload?.message || payload?.content || '';
        if (!text) return;

        const isSelf = senderInfo.userId === currentUserIdInner;
        const senderName = isSelf
          ? 'You'
          : senderInfo.name || senderInfo.userId || senderInfo.userGuid || 'Unknown';

        setChatMessages((prev) => [
          ...prev,
          {
            id: payload.id || payload.msgId || Date.now(),
            sender: senderName,
            text,
            timestamp: payload.timestamp || new Date().toISOString(),
            isSelf,
          },
        ]);

        // Nếu panel chat đang đóng và đây là tin nhắn từ người khác -> show custom toast
        if (!isSelf && !showChat) {
          if (chatToastTimeoutRef.current) {
            clearTimeout(chatToastTimeoutRef.current);
          }
          setChatToast({
            sender: senderName,
            text,
          });
          chatToastTimeoutRef.current = setTimeout(() => {
            setChatToast(null);
          }, 4000);
        }
      } catch (e) {
        console.warn('Error handling chat-on-message payload:', e);
      }
    };

    try {
      client.on('chat-on-message', onChatMessage);
    } catch (e) {
      console.warn('Failed to bind chat-on-message listener on client:', e);
    }

    return () => {
      try {
        client.off('chat-on-message', onChatMessage);
      } catch (e) {
        // ignore
      }
    };
  }, [client, showChat]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;

    const text = chatInput.trim();
    setChatInput('');

    if (chatClient) {
      try {
        // Send to everyone in the session
        await chatClient.sendToAll(text);
      } catch (e) {
        console.warn('Failed to send chat via Zoom:', e);
        message.error(e.message || I18n.t('failed_send_message'));
      }
    }
  };

  return (
    <div className="session-video-wrapper">
      {/* Modal cảnh báo từ khóa nhạy cảm (port từ ced-web) */}
      <Modal
        title={I18n.t('video_call_sensitive_warning_title')}
        visible={!!sensitiveModal}
        closable={false}
        maskClosable={false}
        footer={null}
        width={560}
        className="sensitive-keyword-modal"
        wrapClassName="sensitive-keyword-modal-wrap"
        getContainer={() => document.body}
        zIndex={2147483647}
      >
        {sensitiveModal && (
          <>
            <div className="sensitive-keyword-modal-body">
              <p className="sensitive-keyword-modal-content">
                {I18n.t('video_call_sensitive_warning_content')}
              </p>
              <div className="sensitive-keyword-infographic">
                <img
                  className="sensitive-keyword-infographic-image"
                  src="https://s3.us-west-2.amazonaws.com/staging-ced.kambria.io/UC2+Infographic+16_9.png"
                  alt={I18n.t('video_call_sensitive_guide_link')}
                />
              </div>
            </div>
            <div className="sensitive-keyword-modal-footer">
              <Button
                type="primary"
                block
                className="sensitive-keyword-btn safe"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('SAFE_CONFIRMED')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_continue_safe')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_continue_safe_subtitle')}</span>
              </Button>
              <Button
                block
                className="sensitive-keyword-btn accidental"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('ACCIDENTAL_MENTION')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_continue_careful')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_continue_careful_subtitle')}</span>
              </Button>
              <Button
                danger
                block
                className="sensitive-keyword-btn end"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('end_session')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_stop_call')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_stop_call_subtitle')}</span>
              </Button>
            </div>
          </>
        )}
      </Modal>

      <div className="session-video-main-layout">
        <div className={`session-video-main-section ${mainContent ? 'has-main' : 'no-main'}`}>
          {pinnedUser &&
            (() => {
              const fullPinnedUser = allUsers.find((user) => user.userId === pinnedUser.userId);
              return fullPinnedUser ? renderUserBox(fullPinnedUser, true) : null;
            })()}
          <div
            className={`session-video-share-container ${pinnedUser ? 'has-pin' : 'no-pin'} ${
              activeShareUser ? 'has-share' : 'no-share'
            }`}
          >
            <canvas
              ref={shareCanvasRef}
              id="users-screen-share-content-canvas"
              width={1920}
              height={1080}
              className={`session-video-share-canvas ${shareRenderType === 'canvas' && activeShareUser ? ' show' : ''}`}
            />
            <video
              ref={shareVideoRef}
              id="my-screen-share-content-video"
              width={960}
              height={540}
              className={`session-video-share-video ${shareRenderType === 'video' && activeShareUser ? ' show' : ''}`}
              autoPlay
              playsInline
              muted
            />
          </div>
        </div>
        <div className={`session-video-side-section ${mainContent ? 'has-main' : 'no-main'}`}>
          <div
            className={`${mainContent ? 'session-video-sidebar' : 'session-video-users-list'} ${
              pinnedUser ? 'has-pin' : 'no-pin'
            } ${activeShareUser ? 'has-share' : 'no-share'}`}
          >
            {allUsers.filter((u) => !pinnedUser || u.userId !== pinnedUser.userId).map((user) => renderUserBox(user))}
          </div>
        </div>
      </div>

      {/* User List Panel */}
      {showUserList && (
        <div className="user-list-panel">
          <div className="user-list-header">
            <h5>Participants</h5>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setShowUserList(false)}
              className="close-button"
            />
          </div>
          {allUsers.map((user) => renderUserListItem(user))}
        </div>
      )}

      {/* Chat Panel */}
      {showChat && (
        <div className="chat-panel">
          <div className="chat-header">
            <h5>Chat</h5>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setShowChat(false)}
              className="close-button"
            />
          </div>
          <div className="chat-messages">
            {chatMessages.length === 0 && (
              <div className="chat-empty">No messages yet. Start the conversation!</div>
            )}
            {chatMessages.map((item, index) => {
              const prev = chatMessages[index - 1];
              const showSender = !prev || prev.sender !== item.sender || prev.isSelf !== item.isSelf;

              return (
                <div
                  key={item.id}
                  className={`chat-message ${item.isSelf ? 'self' : 'other'}`}
                >
                  {showSender && (
                    <div className="chat-message-meta">
                      <span className="chat-sender">{item.sender}</span>
                    </div>
                  )}
                  <div className="chat-message-text">{item.text}</div>
                </div>
              );
            })}
          </div>
          <div className="chat-input-wrapper">
            <Input.TextArea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={I18n.t('type_a_message')}
              autoSize={{ minRows: 1, maxRows: 3 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendChat();
                }
              }}
            />
            <Button type="primary" onClick={handleSendChat} disabled={!chatInput.trim()}>
              {I18n.t('send')}
            </Button>
          </div>
        </div>
      )}

      <div className="session-video-controls-bar">
        <Tooltip title={isAudioEnabled ? I18n.t('mute') : I18n.t('unmute')}>
          <Button
            onClick={toggleAudio}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <AudioOutlined />
                {!isAudioEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn audio-toggle-btn"
          />
        </Tooltip>
        <Tooltip title={isVideoEnabled ? I18n.t('stop_video') : I18n.t('start_video')}>
          <Button
            onClick={toggleVideo}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <VideoCameraOutlined />
                {!isVideoEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn video-toggle-btn"
          />
        </Tooltip>
        {!isAnyoneSharing && (
          <Tooltip title={I18n.t('share_screen')}>
            <Button onClick={handleStartShare} style={{ marginRight: 8 }} icon={<ShareAltOutlined />} />
          </Tooltip>
        )}
        {isCurrentUserSharing && (
          <Tooltip title={I18n.t('stop_sharing')}>
            <Button onClick={handleStopShare} style={{ marginRight: 8 }} icon={<StopOutlined />} danger />
          </Tooltip>
        )}
        {isOtherUserSharing && (
          <Tooltip title={I18n.t('screen_sharing_in_progress')} placement="top">
            <Button style={{ marginRight: 8 }} icon={<ShareAltOutlined />} disabled />
          </Tooltip>
        )}
        <Tooltip title={I18n.t('show_participants')}>
          <Button
            onClick={() => {
              const next = !showUserList;
              setShowUserList(next);
              if (next) setShowChat(false);
            }}
            style={{ marginRight: 8 }}
            icon={<UserOutlined />}
            type={showUserList ? 'primary' : 'default'}
          />
        </Tooltip>
        <Tooltip title={showChat ? I18n.t('close_chat') : I18n.t('open_chat')}>
          <Button
            onClick={() => {
              const next = !showChat;
              setShowChat(next);
              if (next) setShowUserList(false);
            }}
            style={{ marginRight: 8 }}
            icon={<MessageOutlined />}
            type={showChat ? 'primary' : 'default'}
          />
        </Tooltip>
        <Tooltip title={I18n.t('leave_session_full')}>
          <Button danger onClick={leaveSession} icon={<PhoneOutlined />} />
        </Tooltip>
      </div>
      {chatToast && (
        <div
          className="chat-toast"
          onClick={() => {
            setShowChat(true);
            setShowUserList(false);
            setChatToast(null);
          }}
        >
          <div className="chat-toast-sender">{chatToast.sender}</div>
          <div className="chat-toast-text">{chatToast.text}</div>
        </div>
      )}
      {loading && (
        <div className="session-video-loading-overlay">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      )}
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });
const mapDispatchToProps = {
  getDetailSession,
  getTokenSession,
  fetchUserMvp,
  verifyJoinSession,
  moderateText,
  logSensitiveKeywordEvent,
};
export default connect(mapStateToProps, mapDispatchToProps)(SessionVideoCustom);
